import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSupabase } from "@/hooks/useSupabase";
import { useAuth } from "@/hooks/useAuth";
import type { Tables } from "@/integrations/supabase/types";

type ProfileRow = Tables<"profiles">;

const RETRY_INTERVAL_MS = 500;
const MAX_RETRIES = 6; // 3 seconds total

export interface ProfileContextValue {
  profile: ProfileRow | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * Reads the shared profile state from {@link ProfileProvider}.
 *
 * Throws if rendered outside the provider so that mis-mounts surface loudly in
 * development rather than silently returning a null profile.
 */
export const useProfileContext = (): ProfileContextValue => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return context;
};

interface ProfileProviderProps {
  children: React.ReactNode;
}

/**
 * Fetches the signed-in user's profile exactly once for the whole app and
 * exposes it via context. Previously every `useProfile()` call site ran its own
 * fetch + create effect, producing duplicate network churn and re-subscription
 * on every render (audit findings P6 + P11). Lifting the logic here means the
 * profile is fetched a single time and shared, and the context value is memoized
 * so consumers only re-render when the profile, loading, or error actually
 * change.
 */
export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const supabase = useSupabase();
  const { userId, isSignedIn, loading, user } = useAuth();
  const isLoaded = !loading;

  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedCreate = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  // Use ref for user properties to avoid dependency changes.
  // Note: firstName/lastName now properly parsed from OAuth full_name in SupabaseAuthProvider.
  const userPropsRef = useRef({
    firstName: user?.firstName || null,
    lastName: user?.lastName || null,
    imageUrl: user?.imageUrl || null,
  });
  userPropsRef.current = {
    firstName: user?.firstName || null,
    lastName: user?.lastName || null,
    imageUrl: user?.imageUrl || null,
  };

  const fetchProfile = useCallback(async () => {
    // Wait for auth to be fully loaded
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn || !userId) {
      setProfile(null);
      setIsLoading(false);
      setError(null);
      inFlightRef.current = null;
      hasAttemptedCreate.current = false;
      lastUserIdRef.current = null;
      return;
    }

    // Prevent overlapping requests
    if (inFlightRef.current) {
      return;
    }

    // Reset guards when the signed-in user changes
    if (lastUserIdRef.current !== userId) {
      lastUserIdRef.current = userId;
      hasAttemptedCreate.current = false;
    }

    setIsLoading(true);
    setError(null);

    const task = (async () => {
      // Poll for profile with retries (profile may not exist yet)
      let retryCount = 0;
      let profileData: ProfileRow | null = null;

      while (retryCount < MAX_RETRIES) {
        const { data, error: fetchError } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle();

        if (fetchError) {
          console.error("[useProfile] Fetch error:", fetchError.message, fetchError);
          // Don't retry on actual errors, only on "not found"
          if (fetchError.code !== "PGRST116") {
            setError(fetchError.message);
            setIsLoading(false);
            return;
          }
        }

        if (data && "user_id" in data) {
          profileData = data as unknown as ProfileRow;
          break;
        }

        // Profile not found yet - wait and retry
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
        }
      }

      if (profileData) {
        setProfile(profileData);
        setIsLoading(false);
        return;
      }

      // Profile still not found after waiting - create it directly (only once)
      if (hasAttemptedCreate.current) {
        console.warn("[useProfile] Already attempted to create profile, skipping");
        setError("Profile creation failed. Please contact support if this persists.");
        setIsLoading(false);
        return;
      }

      hasAttemptedCreate.current = true;

      const { data: newProfile, error: createError } = await supabase
        .from("profiles")
        .insert({
          user_id: userId,
          first_name: userPropsRef.current.firstName,
          last_name: userPropsRef.current.lastName,
          avatar_url: userPropsRef.current.imageUrl,
          account_type: "individual",
          role: "rep",
        })
        .select()
        .single();

      if (createError) {
        console.error("[useProfile] Create error:", createError.message, createError);

        // Check if it's a duplicate key error (profile was created elsewhere)
        if (createError.code === "23505") {
          const { data: existingProfile } = await supabase
            .from("profiles")
            .select("*")
            .eq("user_id", userId)
            .single();

          if (existingProfile && "user_id" in existingProfile) {
            setProfile(existingProfile as unknown as ProfileRow);
            setIsLoading(false);
            return;
          }
        }

        setError(createError.message || "Failed to create profile. Please try again.");
        setIsLoading(false);
        return;
      }

      if (newProfile && "user_id" in newProfile) {
        setProfile(newProfile as unknown as ProfileRow);
      }

      setIsLoading(false);
    })()
      .catch((err) => {
        console.error("[useProfile] Unexpected error:", err);
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
        setIsLoading(false);
      })
      .finally(() => {
        inFlightRef.current = null;
      });

    inFlightRef.current = task;
    await task;
  }, [isSignedIn, isLoaded, supabase, userId]);

  useEffect(() => {
    // Wait for auth to be loaded
    if (loading || !isLoaded) return;

    if (!isSignedIn || !userId) {
      setProfile(null);
      setIsLoading(false);
      setError(null);
      inFlightRef.current = null;
      hasAttemptedCreate.current = false;
      lastUserIdRef.current = null;
      return;
    }

    // Fire-and-forget: fetchProfile handles its own errors and sets error state.
    void fetchProfile();
  }, [fetchProfile, isSignedIn, loading, userId, isLoaded]);

  const value = useMemo<ProfileContextValue>(
    () => ({
      profile,
      isLoading: isLoading || loading,
      error,
      refresh: fetchProfile,
    }),
    [profile, isLoading, loading, error, fetchProfile],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export default ProfileProvider;
